  import React, { useState } from 'react';
  import ExcelJS from 'exceljs';
  import { Box, Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useModals from './modals/useModal';
import { modals } from './modals/Modals';

  const CommonLargeExcelDownload = ({
    title = "Download",
    excelHeader = [],
    excelParam = {},              // 부모로부터 받는 검색 조건 객체 ({status: '', sort: 'opStatus,asc'})
    fetchApiFn = null,            // 백엔드 AXIOS 호출 함수
    mappingFn = null,             // 데이터 매핑 함수
    apiKey = "maintenanceTasks",  // JSON 응답 타겟 키 이름
    pageSize = 5000,               // 쪼개어 가져올 단위
    totalCount = 0
  }) => {
    const { t } = useTranslation();
    const [finish, setFinish] = useState('standby');
    const filename = `${title}_${Date.now()}.xlsx`;

    // modal 관련
    const { openModal } = useModals();
    const handleAlertClick = (alertMessage) => {
        openModal(modals.alertModal, {
            onClose: () => {},
            message: alertMessage,
        });
    };

    const getColName = (index) => {
      if (index < 26) {
        return String.fromCharCode(65 + index);
      } else {
        let firstStr = String.fromCharCode(65 + parseInt(index / 26) - 1);
        let secondStr = String.fromCharCode(65 + (index % 26));
        return firstStr + secondStr;
      }
    };

    // 🌟 [핵심 수정] 문자열이 아니라 순수 '객체(Object)'를 조립해주는 헬퍼 함수
    // 문자열과 객체, 그리고 중복 파라미터까지 완벽하게 처리하는 최종 헬퍼 함수
    // 중복 파라미터에서 대괄호([])를 제거하고 백엔드가 원하는 순수 형태로 조립하는 헬퍼 함수
    // [최종 해결 버전] 부모 Axios의 Object 요구사항과 백엔드의 대괄호 없는 순수 포맷을 모두 만족하는 함수
    const buildQueryObject = (baseParam, targetPage, targetSize) => {
      let paramsObj = {
        page: targetPage,
        size: targetSize
      };

      if (baseParam) {
        if (typeof baseParam === 'string') {
          console.log(baseParam)
          const cleanParam = baseParam.startsWith('?') ? baseParam.slice(1) : baseParam;
          const searchParams = new URLSearchParams(cleanParam);
          for (let [key, value] of searchParams.entries()) {
            if (paramsObj.hasOwnProperty(key)) {
              if (Array.isArray(paramsObj[key])) {
                paramsObj[key].push(value);
              } else {
                paramsObj[key] = [paramsObj[key], value];
              }
            } else {
              paramsObj[key] = value;
            }
          }
        } else if (typeof baseParam === 'object') {
          // 객체로 들어온 경우 중복 처리 구조가 이미 잡혀있다면 그대로 병합
          paramsObj = { ...baseParam, ...paramsObj };
        }
      }

      paramsObj.page = targetPage;
      paramsObj.size = targetSize;

      return paramsObj; // 🌟 꼼수 없는 100% 순수 객체 반환
    };

    const makeAndDownloadExcel = (fetchedDatas, totalElements) => {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Sheet1', {
        pageSetup: { paperSize: 9, orientation: 'landscape', horizontalCentered: true },
      });
      ws.pageSetup.margins = { left: 0.7, right: 0.7, top: 0.75, bottom: 0.75, header: 0.3, footer: 0.3 };

      let excelData = [{ data: [title] }, { data: [] }, { data: excelHeader }];
      fetchedDatas.forEach((item) => {
        excelData.push({ data: item });
      });

      excelData.forEach((item, index) => {
        ws.getRow(index + 1).values = [...item.data];
      });

      const lastColLetter = getColName(excelHeader.length - 1);
      ws.mergeCells(`A1:${lastColLetter}1`);
      ws.getCell('A1').font = { size: 18, bold: true, color: { argb: '000000' } };
      ws.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };

      excelHeader.forEach((_, index) => {
        const colLetter = getColName(index);
        const headerCell = ws.getCell(`${colLetter}3`);
        headerCell.font = { bold: true };
        headerCell.alignment = { horizontal: 'center', vertical: 'middle' };
        headerCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'e0e0e0' } };
        headerCell.border = { top: { style: 'medium', color: { argb: '000000' } } };
      });

      let dataStartIdx = 4;
      fetchedDatas.forEach((_, rowIndex) => {
        const currentRowNum = rowIndex + dataStartIdx;
        excelHeader.forEach((_, colIndex) => {
          const colLetter = getColName(colIndex);
          const cell = ws.getCell(`${colLetter}${currentRowNum}`);
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          cell.border = {
            top: { style: 'thin', color: { argb: '000000' } },
            ...(rowIndex === fetchedDatas.length - 1 && { bottom: { style: 'thin', color: { argb: '000000' } } })
          };
        });
      });

      ws.columns = excelHeader.map(() => ({ key: 'data', width: 15 }));
      for (let i = 1; i <= excelData.length; i++) {
        ws.getRow(i).height = 22;
      }

      wb.xlsx.writeBuffer().then((b) => {
        let a = new Blob([b]);
        let url = window.URL.createObjectURL(a);
        let elem = document.createElement('a');
        elem.href = url;
        elem.download = filename;
        elem.click();
        elem.remove();
        window.URL.revokeObjectURL(url);
        setFinish('standby');
      });
    };

    const download = async () => {
      if (typeof fetchApiFn !== 'function' || typeof mappingFn !== 'function') {
        console.error("CommonExcelDownload Error: 필수 함수가 지정되지 않았습니다.");
        return;
      }

      setFinish('progress');
      let allParsedDatas = [];

      try {
        const firstQueryObj = buildQueryObject(excelParam, 0, pageSize);
        const firstResponse = await fetchApiFn(firstQueryObj);

        if (!firstResponse) {
          handleAlertClick(t('message.common.text.noData'));
          setFinish('standby');
          return;
        }

        // 🌟 [핵심 분기 포인트] 페이징(HATEOAS) API인지 일반 배열 API인지 판별
        const isPagedApi = firstResponse._embedded && firstResponse.page;

        if (isPagedApi) {
          // ==========================================
          // 케이스 1: 기존의 대용량 페이징(HATEOAS) API인 경우
          // ==========================================
          const totalElements = firstResponse.page.totalElements;
          const firstList = firstResponse._embedded[apiKey] || [];
          
          if (firstList.length === 0) {
            handleAlertClick(t('message.common.text.noData'));
            setFinish('standby');
            return;
          }

          const parsedFirstPage = firstList.map((item, index) => mappingFn(item, index, totalElements, 0));
          allParsedDatas = [...allParsedDatas, ...parsedFirstPage];

          const totalPages = Math.ceil(totalElements / pageSize);

          // 2페이지 이상이 있으면 Promise.all로 쪼개서 동시 호출
          if (totalPages > 1) {
            const promises = [];
            for (let pageIdx = 1; pageIdx < totalPages; pageIdx++) {
              const nextQueryObj = buildQueryObject(excelParam, pageIdx, pageSize);
              promises.push(fetchApiFn(nextQueryObj));
            }

            const remainingResponses = await Promise.all(promises);

            remainingResponses.forEach((response, index) => {
              if (response && response._embedded && response._embedded[apiKey]) {
                const nextList = response._embedded[apiKey];
                const startIndex = (index + 1) * pageSize;
                
                const parsedNextPage = nextList.map((item, idx) => mappingFn(item, idx, totalElements, startIndex));
                allParsedDatas = [...allParsedDatas, ...parsedNextPage];
              }
            });
          }

          // 엑셀 빌드 함수 실행
          makeAndDownloadExcel(allParsedDatas, totalElements);

        } else {
          // ==========================================
          // 케이스 2: 페이징이 없는 일반 목록 API인 경우 
          // (응답 자체가 배열이거나, response.data 형태 등일 때)
          // ==========================================
          // 백엔드 응답이 순수 배열 `[...]` 형태이거나 혹은 객체 내 데이터 필드일 경우를 모두 고려
          const dataList = Array.isArray(firstResponse) 
            ? firstResponse 
            : (firstResponse.data || firstResponse.content || firstResponse[apiKey] || []);

          if (dataList.length === 0) {
            handleAlertClick(t('message.common.text.noData'));
            setFinish('standby');
            return;
          }

          const totalElements = dataList.length;
          // 페이징이 없으므로 단 한 번에 전체 매핑 처리
          allParsedDatas = dataList.map((item, index) => mappingFn(item, index, totalElements, 0));

          // 엑셀 빌드 함수 실행
          makeAndDownloadExcel(allParsedDatas, totalElements);
        }

      } catch (err) {
        console.error("Error occurred during shared bulk Excel download.:", err);
        handleAlertClick("An error occurred during the download.");
        setFinish('standby');
      }
    };

    if (totalCount <= 0) {
        return null; // 데이터가 없으면 아무것도 렌더링하지 않음 (버튼 숨김)
    }

    return (
      (finish === 'standby' || finish === 'end') ?
        <Button variant="contained" color="success" onClick={download} style={{ margin: "3px" }}>
          {t('message.common.btn.excel.download')}
        </Button>
        :
        <div className='no-print-page' style={{ color: "#fff" }}>
          <Box sx={{ display: 'flex', color: "#fff", alignItems: 'center', gap: '8px', margin: "3px" }}>
            {t('message.common.text.excel.downloading')}... <CircularProgress size="1rem" />
          </Box>
        </div>
    );
  };

  export default CommonLargeExcelDownload;